/**
 * KonsolMismatchPanel.jsx — src/components/Finance/KonsolMismatchPanel.jsx
 * Расхождения «у нас выплачено — в Консоли не оплачено».
 * Именно эти строки и порождают вопросы исполнителей.
 */
import { useCallback, useContext, useEffect, useState } from 'react';
import {
    Box, Button, Chip, CircularProgress, Sheet, Stack, Table, Typography,
} from '@mui/joy';

import SyncRoundedIcon from '@mui/icons-material/SyncRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';

import { APIKonsolMismatches, APIKonsolReconcile } from '../../../api';
import { LayoutContext } from '../../../context';

const STATUS_LABELS = {
    submitted: 'Отправлено исполнителю',
    created: 'Создано',
    accepted: 'Принято исполнителем',
    confirmed_by_company: 'Подтверждено компанией',
    paid: 'Оплачено',
    declined: 'Отклонено',
    declined_by_company: 'Отклонено компанией',
    revoked: 'Отозвано',
    canceled: 'Отменено',
    cancelled: 'Отменено',
    expired: 'Истекло',
};

const ROLE_LABELS = { surveyor: 'Обмерщик', draftsman: 'Чертёжник' };

export default function KonsolMismatchPanel() {
    const { tokenEmployee, notify } = useContext(LayoutContext);

    const [rows, setRows] = useState([]);
    const [count, setCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [syncing, setSyncing] = useState(false);
    const [expanded, setExpanded] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        const data = await APIKonsolMismatches(tokenEmployee, 500);
        setRows(data?.results || []);
        setCount(data?.count || 0);
        setLoading(false);
    }, [tokenEmployee]);

    useEffect(() => { load(); }, [load]);

    const handleSync = async () => {
        setSyncing(true);
        const res = await APIKonsolReconcile(tokenEmployee, 300);
        setSyncing(false);

        if (res?.status !== 'ok') {
            notify('Не удалось выполнить сверку с Консолью', 'danger');
            return;
        }
        const s = res.stats || {};
        notify(
            `Сверка выполнена: проверено ${s.checked || 0}, обновлено ${s.changed || 0}, стало оплачено ${s.now_paid || 0}`,
            'success',
        );
        load();
    };

    return (
        <Sheet sx={{ borderRadius: '18px', border: '1px solid', borderColor: 'divider', p: 2, mb: 2 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between">
                <Stack direction="row" spacing={1.2} alignItems="center">
                    <Box sx={{
                        width: 38, height: 38, borderRadius: '12px', flexShrink: 0,
                        background: count > 0 ? 'rgba(239,68,68,0.12)' : 'rgba(22,163,74,0.12)',
                        color: count > 0 ? '#ef4444' : '#16a34a',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        {count > 0 ? <WarningAmberRoundedIcon /> : <CheckCircleRoundedIcon />}
                    </Box>
                    <Box>
                        <Typography sx={{ fontWeight: 800, fontSize: '0.95rem' }}>
                            Расхождения с Консолью
                        </Typography>
                        <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                            {loading
                                ? 'Загружаем…'
                                : count > 0
                                    ? `${count} выплат отмечены как оплаченные, но в Консоли не оплачены`
                                    : 'Все оплаченные выплаты подтверждены в Консоли'}
                        </Typography>
                    </Box>
                </Stack>

                <Stack direction="row" spacing={1}>
                    <Button
                        size="sm" variant="soft" loading={syncing}
                        startDecorator={<SyncRoundedIcon sx={{ fontSize: 17 }} />}
                        onClick={handleSync}
                    >
                        Сверить сейчас
                    </Button>
                    {count > 0 && (
                        <Button size="sm" variant="plain" onClick={() => setExpanded(v => !v)}>
                            {expanded ? 'Свернуть' : 'Показать'}
                        </Button>
                    )}
                </Stack>
            </Stack>

            {expanded && (
                <Box sx={{ mt: 2, overflow: 'auto' }}>
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                            <CircularProgress size="sm" />
                        </Box>
                    ) : (
                        <Table
                            size="sm"
                            sx={{
                                '--TableCell-headBackground': 'rgba(99,102,241,0.05)',
                                '& thead th': { fontWeight: 800, fontSize: '0.72rem', whiteSpace: 'nowrap' },
                                '& tbody td': { fontSize: '0.8rem' },
                                minWidth: 620,
                            }}
                        >
                            <thead>
                                <tr>
                                    <th style={{ width: 110 }}>Выплата</th>
                                    <th style={{ width: 110 }}>Событие</th>
                                    <th style={{ width: 120 }}>Роль</th>
                                    <th style={{ width: 130 }}>Задание</th>
                                    <th>Статус в Консоли</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r, i) => (
                                    <tr key={`${r.payment_event_id}-${r.role}-${i}`}>
                                        <td>#{r.payment_event_id}</td>
                                        <td>#{r.event_id}</td>
                                        <td>{ROLE_LABELS[r.role] || r.role}</td>
                                        <td>{r.task_id || <Chip size="sm" color="danger" variant="soft">нет задания</Chip>}</td>
                                        <td>
                                            <Chip size="sm" variant="soft" color={r.konsol_status ? 'warning' : 'danger'}>
                                                {STATUS_LABELS[r.konsol_status] || r.konsol_status || 'не создано'}
                                            </Chip>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                </Box>
            )}
        </Sheet>
    );
}